import React, { useState } from 'react';
import axios from 'axios';
import {
  FluentProvider, webLightTheme,
  Text, Button, Input, Spinner,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { TIPO_CLIENTE, PAESI } from '../constants/anagrafica';

const api = axios.create({ withCredentials: true });

const TIPO_OPTIONS = TIPO_CLIENTE;

const PROVINCE = [
  'AG','AL','AN','AO','AP','AQ','AR','AT','AV','BA','BG','BI','BL','BN','BO','BR','BS','BT','BZ',
  'CA','CB','CE','CH','CL','CN','CO','CR','CS','CT','CZ','EN','FC','FE','FG','FI','FM','FR','GE',
  'GO','GR','IM','IS','KR','LC','LE','LI','LO','LT','LU','MB','MC','ME','MI','MN','MO','MS','MT',
  'NA','NO','NU','OG','OR','OT','PA','PC','PD','PE','PG','PI','PN','PO','PR','PT','PU','PV','PZ',
  'RA','RC','RE','RG','RI','RM','RN','RO','SA','SI','SO','SP','SR','SS','SU','SV','TA','TE','TN',
  'TO','TP','TR','TS','TV','UD','VA','VB','VC','VE','VI','VR','VS','VT','VV',
];

const selStyle = { width: '100%', height: 36, border: '1px solid #d1d1d1', borderRadius: 4, padding: '0 8px', fontSize: 14, fontFamily: 'inherit', color: '#4d5592', background: '#fff' };

const F = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#4d5592', marginBottom: 4 }}>
      {label}{required && <span style={{ color: '#c11b1b' }}> *</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{hint}</div>}
  </div>
);
const Row = ({ children }) => <div style={{ display: 'flex', gap: 16 }}>{children}</div>;
const Col = ({ flex, children }) => <div style={{ flex: flex || 1 }}>{children}</div>;

const SignupCompletePage = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  // Se il profilo è già completo, redirige alla home
  React.useEffect(() => {
    api.get('/api/profilo')
      .then(res => {
        const d = res.data;
        if (d && d.nome && d.cognome && d.nome !== d.email?.split('@')[0]) {
          window.location.href = '/';
        } else {
          setChecking(false);
          // Pre-fill nome/cognome se disponibili
          if (d?.nome) setNome(d.nome);
          if (d?.cognome) setCognome(d.cognome);
        }
      })
      .catch(() => setChecking(false));
  }, []);

  const [tipo, setTipo] = useState('privato');
  const [cognome, setCognome] = useState('');
  const [nome, setNome] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [cap, setCap] = useState('');
  const [citta, setCitta] = useState('');
  const [provincia, setProvincia] = useState('');
  const [nazione, setNazione] = useState('IT');
  const [prefissoTelefono, setPrefissoTelefono] = useState('+39');
  const [telefono, setTelefono] = useState('');
  const [organizzazione, setOrganizzazione] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [codiceFiscaleOrg, setCodiceFiscaleOrg] = useState('');
  const [codiceSdi, setCodiceSdi] = useState('');
  const [pec, setPec] = useState('');

  const isBusiness = tipo !== 'privato';

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!cognome.trim() || !nome.trim()) { setError('Cognome e nome sono obbligatori'); return; }
    setError(''); setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.post('/api/completa-registrazione', {
        tipo_cliente: tipo, nome: nome.trim(), cognome: cognome.trim(),
        ragione_sociale: isBusiness ? organizzazione.trim() : null,
        partita_iva: isBusiness ? partitaIva : '', codice_fiscale: codiceFiscale,
        codice_fiscale_org: isBusiness ? codiceFiscaleOrg : '',
        indirizzo, cap, citta, provincia, telefono, prefisso_telefono: prefissoTelefono,
        nazione, codice_sdi: codiceSdi, pec,
      });
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.messaggio || 'Errore durante il salvataggio'); setLoading(false);
    }
  };

  const stepTitles = ['Identità', 'Coordinate'];

  if (checking) {
    return (
      <FluentProvider theme={webLightTheme}>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner size="large" />
        </div>
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb' }}>
        <a href="https://neoflux.it" style={{ textDecoration: 'none' }}><img src="/icona.svg" alt="NeoFlux" style={{ height: 32 }} /></a>
      </div>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ display: 'flex', gap: 0, marginBottom: 40 }}>
          {stepTitles.map((title, i) => (
            <div key={i} style={{ flex: 1, cursor: i < step ? 'pointer' : 'default' }} onClick={() => { if (i < step) setStep(i); }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: `3px solid ${i <= step ? '#000e9c' : '#e5e7eb'}`, opacity: i <= step ? 1 : 0.5 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: i < step ? '#268403' : i === step ? '#000e9c' : '#e5e7eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: 16, fontWeight: 600, color: i <= step ? '#00185e' : '#999' }}>{title}</span>
              </div>
            </div>
          ))}
        </div>

        {error && <MessageBar intent="error" style={{ marginBottom: 20 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}

        {step === 0 && (
          <form onSubmit={handleStep1}>
            <F label="Tipo di account" required>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={selStyle}>
                {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </F>
            <Row>
              <Col><F label="Cognome" required><Input value={cognome} onChange={(e, d) => setCognome(d.value)} required style={{ width: '100%' }} autoFocus /></F></Col>
              <Col><F label="Nome" required><Input value={nome} onChange={(e, d) => setNome(d.value)} required style={{ width: '100%' }} /></F></Col>
            </Row>
            <F label="Codice Fiscale Personale" hint="CF del titolare/legale rappresentante">
              <Input value={codiceFiscale} onChange={(e, d) => setCodiceFiscale(d.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} style={{ width: '100%' }} />
            </F>
            {isBusiness && (
              <>
                <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0', paddingTop: 16 }}><Text weight="semibold" style={{ color: '#00185e', fontSize: 16 }}>Attività</Text></div>
                <F label="Organizzazione" required><Input value={organizzazione} onChange={(e, d) => setOrganizzazione(d.value)} required style={{ width: '100%' }} /></F>
                <Row>
                  <Col><F label="Codice fiscale organizzazione"><Input value={codiceFiscaleOrg} onChange={(e, d) => setCodiceFiscaleOrg(d.value.toUpperCase())} style={{ width: '100%' }} /></F></Col>
                  <Col><F label="Partita IVA comunitaria"><Input value={partitaIva} onChange={(e, d) => setPartitaIva(d.value.toUpperCase())} placeholder="IT04886720616" style={{ width: '100%' }} /></F></Col>
                </Row>
              </>
            )}
            <div style={{ marginTop: 32 }}>
              <Button appearance="primary" type="submit" disabled={!cognome.trim() || !nome.trim() || (isBusiness && !organizzazione.trim())} style={{ height: 44, fontSize: 16, fontWeight: 700, width: '100%' }}>Step successivo</Button>
            </div>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={handleSubmit}>
            <F label="Indirizzo"><Input value={indirizzo} onChange={(e, d) => setIndirizzo(d.value)} style={{ width: '100%' }} autoFocus /></F>
            <Row>
              <Col flex={1}><F label="Codice Postale"><Input value={cap} onChange={(e, d) => setCap(d.value)} style={{ width: '100%' }} /></F></Col>
              <Col flex={2}><F label="Città"><Input value={citta} onChange={(e, d) => setCitta(d.value)} style={{ width: '100%' }} /></F></Col>
            </Row>
            <Row>
              <Col><F label="Provincia">
                <select value={provincia} onChange={(e) => setProvincia(e.target.value)} style={selStyle}>
                  <option value="">Seleziona provincia</option>
                  {PROVINCE.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </F></Col>
              <Col><F label="Nazione">
                <select value={nazione} onChange={(e) => setNazione(e.target.value)} style={selStyle}>
                  {PAESI.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                </select>
              </F></Col>
            </Row>
            <F label="Telefono">
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 80 }}><Input value={prefissoTelefono} onChange={(e, d) => setPrefissoTelefono(d.value)} placeholder="+39" style={{ width: '100%' }} /></div>
                <div style={{ flex: 1 }}><Input value={telefono} onChange={(e, d) => setTelefono(d.value)} type="tel" style={{ width: '100%' }} /></div>
              </div>
            </F>
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0', paddingTop: 16 }}><Text weight="semibold" style={{ color: '#00185e', fontSize: 16 }}>Fatturazione elettronica</Text></div>
            <Row>
              <Col><F label="SDI (Codice Destinatario)" hint="7 caratteri"><Input value={codiceSdi} onChange={(e, d) => setCodiceSdi(d.value.toUpperCase())} placeholder="0000000" maxLength={7} style={{ width: '100%' }} /></F></Col>
              <Col><F label="PEC"><Input value={pec} onChange={(e, d) => setPec(d.value)} type="email" style={{ width: '100%' }} /></F></Col>
            </Row>
            <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
              <Button appearance="primary" type="submit" disabled={loading} style={{ height: 44, fontSize: 16, fontWeight: 700, flex: 1 }}>
                {loading ? <Spinner size="tiny" /> : 'Crea un account'}
              </Button>
              <Button appearance="subtle" type="button" onClick={() => setStep(0)} style={{ height: 44 }}>Indietro</Button>
            </div>
          </form>
        )}

        <div style={{ marginTop: 16, fontSize: 12, color: '#4d5592' }}><span style={{ color: '#c11b1b', fontWeight: 700 }}>*</span> campi obbligatori</div>
      </div>
    </div>
    </FluentProvider>
  );
};

export default SignupCompletePage;
